import { z } from 'zod';
import { lookSchema } from './look.js';

/**
 * Relationships between players: marriage, dating, siblings and best friends.
 *
 * Both players must agree. One proposes a kind and picks the role each of
 * them will hold (say husband and wife); the other accepts or declines.
 * Either can end it at any time, and blocking someone ends it too. Only
 * accepted relationships appear on a player's profile.
 */

export const RELATIONSHIP_KINDS = ['married', 'dating', 'siblings', 'bestfriends'] as const;
export type RelationshipKind = (typeof RELATIONSHIP_KINDS)[number];
export const relationshipKindSchema = z.enum(RELATIONSHIP_KINDS);

export const RELATIONSHIP_ROLES = ['husband', 'wife', 'boyfriend', 'girlfriend', 'brother', 'sister', 'bestfriend'] as const;
export type RelationshipRole = (typeof RELATIONSHIP_ROLES)[number];
export const relationshipRoleSchema = z.enum(RELATIONSHIP_ROLES);

export interface RelationshipDef {
  kind: RelationshipKind;
  label: string;
  /** Roles each side may hold. */
  roles: readonly RelationshipRole[];
  /** How many of these one player can have at once. */
  max: number;
  /** Married and dating share one slot: a player has at most one partner. */
  partner: boolean;
}

export const RELATIONSHIPS: Readonly<Record<RelationshipKind, RelationshipDef>> = Object.freeze({
  married: { kind: 'married', label: 'Married', roles: ['husband', 'wife'], max: 1, partner: true },
  dating: { kind: 'dating', label: 'Dating', roles: ['boyfriend', 'girlfriend'], max: 1, partner: true },
  siblings: { kind: 'siblings', label: 'Siblings', roles: ['brother', 'sister'], max: 3, partner: false },
  bestfriends: { kind: 'bestfriends', label: 'Best friends', roles: ['bestfriend'], max: 3, partner: false },
});

export const ROLE_LABELS: Readonly<Record<RelationshipRole, string>> = Object.freeze({
  husband: 'Husband',
  wife: 'Wife',
  boyfriend: 'Boyfriend',
  girlfriend: 'Girlfriend',
  brother: 'Brother',
  sister: 'Sister',
  bestfriend: 'Best friend',
});

/** Dating and marriage: the kinds that need an operator to switch them on. */
export const ROMANTIC_KINDS: readonly RelationshipKind[] = RELATIONSHIP_KINDS.filter((kind) => RELATIONSHIPS[kind].partner);

/**
 * The kinds a player may start. Kopi Kaki takes players from 13 and never asks
 * their age, so romance between players is off unless the operator opts in.
 */
export function proposableKinds(allowRomantic: boolean): RelationshipKind[] {
  return RELATIONSHIP_KINDS.filter((kind) => allowRomantic || !ROMANTIC_KINDS.includes(kind));
}

/** Proposals a player may have waiting for an answer. */
export const MAX_PENDING_PROPOSALS = 5;

export function isRoleAllowed(kind: RelationshipKind, role: string): role is RelationshipRole {
  return (RELATIONSHIPS[kind].roles as readonly string[]).includes(role);
}

export const proposeRelationshipSchema = z
  .object({
    userId: z.string().min(1).max(64),
    kind: relationshipKindSchema,
    /** The role the proposer takes. */
    myRole: relationshipRoleSchema,
    /** The role the other player would take. */
    theirRole: relationshipRoleSchema,
  })
  .refine((body) => isRoleAllowed(body.kind, body.myRole) && isRoleAllowed(body.kind, body.theirRole), {
    message: 'That role does not belong to this kind of relationship',
  });

export interface RelationshipView {
  id: string;
  kind: RelationshipKind;
  status: 'pending' | 'active';
  /** True when this player made the proposal. */
  iProposed: boolean;
  other: { id: string; displayName: string; look: z.infer<typeof lookSchema> };
  /** The role I hold, and the role the other player holds. */
  myRole: RelationshipRole;
  theirRole: RelationshipRole;
  since: number;
}

/** Shown on a public profile: just what the other player is to them. */
export interface PublicRelationship {
  id: string;
  kind: RelationshipKind;
  otherId: string;
  otherName: string;
  /** What the other player is to the profile's owner, e.g. 'wife'. */
  otherRole: RelationshipRole;
  since: number;
}

export const relationshipServerMessageSchema = z.discriminatedUnion('t', [
  z.object({
    t: z.literal('rel:proposal'),
    id: z.string(),
    kind: relationshipKindSchema,
    /** The role the recipient would hold. */
    yourRole: relationshipRoleSchema,
    from: z.object({ id: z.string(), displayName: z.string(), look: lookSchema }),
  }),
  /** A relationship was accepted, declined or ended: refetch. */
  z.object({ t: z.literal('rel:changed') }),
]);
export type RelationshipServerMessage = z.infer<typeof relationshipServerMessageSchema>;
